import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const JobHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0052CC 0%, #0747A6 100%)',
  padding: theme.spacing(6, 0),
  color: 'white',
  marginBottom: theme.spacing(4),
}));

const mockJob = {
  id: 1,
  title: 'Senior Frontend Developer',
  company: 'Tech Corp',
  location: 'New York, NY',
  type: 'Full-time',
  salary: '$80k-$120k',
  posted: '2 days ago',
  description: `We are looking for an experienced Frontend Developer to join our team. The ideal candidate will have strong experience with modern web technologies and a passion for creating exceptional user experiences.

Key Responsibilities:
• Develop and maintain responsive web applications
• Collaborate with design team to implement UI/UX designs
• Write clean, maintainable, and efficient code
• Participate in code reviews and technical discussions
• Mentor junior developers

Requirements:
• 5+ years of experience with modern web technologies
• Strong proficiency in React, TypeScript, and Node.js
• Experience with state management solutions (Redux, MobX)
• Understanding of REST APIs and microservices architecture
• Excellent problem-solving and communication skills`,
  tags: ['React', 'TypeScript', 'Node.js', 'Redux', 'REST APIs'],
  benefits: [
    'Competitive salary',
    'Health insurance',
    '401(k) matching',
    'Flexible work hours',
    'Remote work options',
    'Professional development',
    'Team events',
  ],
  companyInfo: {
    name: 'Tech Corp',
    description: 'Tech Corp is a leading technology company specializing in innovative solutions...',
    size: '100-500 employees',
    industry: 'Technology',
    founded: '2015',
  },
};

function JobViewPage() {
  return (
    <Box>
      <JobHeader>
        <Container>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom>
                {mockJob.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body1">
                  <BusinessIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockJob.company}
                </Typography>
                <Typography variant="body1">
                  <LocationOnIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockJob.location}
                </Typography>
                <Typography variant="body1">
                  <WorkIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockJob.type}
                </Typography>
                <Typography variant="body1">
                  <AttachMoneyIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {mockJob.salary}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'white' } }}
              >
                Apply Now
              </Button>
            </Grid>
          </Grid>
        </Container>
      </JobHeader>

      <Container>
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Job Description
                </Typography>
                <Typography variant="body1" paragraph>
                  {mockJob.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {mockJob.tags.map((tag) => (
                    <Chip key={tag} label={tag} color="primary" variant="outlined" />
                  ))}
                </Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Benefits
                </Typography>
                <List>
                  {mockJob.benefits.map((benefit) => (
                    <ListItem key={benefit}>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Apply for this position
                </Typography>
                <Box component="form" sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone"
                        type="tel"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cover Letter"
                        multiline
                        rows={4}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                      >
                        Submit Application
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Company Information
                </Typography>
                <Typography variant="body1" paragraph>
                  {mockJob.companyInfo.description}
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <BusinessIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Company Size"
                      secondary={mockJob.companyInfo.size}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WorkIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Industry"
                      secondary={mockJob.companyInfo.industry}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AccessTimeIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Founded"
                      secondary={mockJob.companyInfo.founded}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Similar Jobs
                </Typography>
                <List>
                  <ListItem button>
                    <ListItemText
                      primary="Frontend Developer"
                      secondary="Tech Corp • New York, NY • $70k-$100k"
                    />
                  </ListItem>
                  <ListItem button>
                    <ListItemText
                      primary="UI/UX Developer"
                      secondary="Design Studio • Remote • $75k-$110k"
                    />
                  </ListItem>
                  <ListItem button>
                    <ListItemText
                      primary="React Developer"
                      secondary="Web Solutions • San Francisco, CA • $85k-$125k"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default JobViewPage; 